<script>
import { mapGetters } from 'vuex';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { getInboxClassByType } from 'dashboard/helper/inbox';
import Thumbnail from '../Thumbnail.vue';
import MessagePreview from './MessagePreview.vue';
import router from '../../../routes';
import { frontendURL, conversationUrl } from '../../../helper/URLHelper';
import InboxName from '../InboxName.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import PriorityMark from './PriorityMark.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import StarIcon from './CardCsat/StarIcon.vue';
import SvgIcon from 'dashboard/components-next/SvgIcon/index.vue';
import Modal from '../../Modal.vue';


export default {
  components: {
    StarIcon,
    CardLabels,
    InboxName,
    Thumbnail,
    ConversationContextMenu,
    TimeAgo,
    MessagePreview,
    PriorityMark,
    SLACardLabel,
    ContextMenu,
    SvgIcon,
    Modal,
  },
  mixins: [inboxMixin],
  setup() {
    return {};
  },
  props: {
    activeLabel: {
      type: String,
      default: '',
    },
    chat: {
      type: Object,
      default: () => {},
    },
    hideInboxName: {
      type: Boolean,
      default: false,
    },
    hideThumbnail: {
      type: Boolean,
      default: false,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showAssignee: {
      type: Boolean,
      default: false,
    },
    conversationType: {
      type: String,
      default: '',
    },
    selected: {
      type: Boolean,
      default: false,
    },
    enableContextMenu: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'contextMenuToggle',
    'assignAgent',
    'assignLabel',
    'assignTeam',
    'markAsUnread',
    'assignPriority',
    'updateConversationStatus',
  ],
  data() {
    return {
      hovered: false,
      showContextMenu: false,
      contextMenu: {
        x: null,
        y: null,
      },
      constants: wootConstants,
      isVisible: false,
      observer: null,
      reportedConversations: new Set(), // 跟踪已上报的对话ID
      timeUpdateInterval: null, // 时间更新定时器
      currentTimeDisplayText: '', // 当前显示的时间文本
      showResolveConfirm: false,
      pendingSnoozedUntil: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      inboxesList: 'inboxes/getInboxes',
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
    }),
    bulkActionCheck() {
      return !this.hideThumbnail && !this.hovered && !this.selected;
    },
    chatMetadata() {
      return this.chat.meta || {};
    },

    assignee() {
      return this.chatMetadata.assignee || {};
    },

    /**
     * Returns the display name for the assignee. If the chat is resolved and there is no assignee name, returns 'AI'.
     */
    assigneeDisplayName() {
      if (
        this.chat.status === this.constants.STATUS_TYPE.RESOLVED &&
        !this.assignee.name
      ) {
        return 'AI';
      }
      return this.assignee.name;
    },

    currentContact() {
      const sender = this.chatMetadata.sender;
      if (!sender) return {};
      return this.$store.getters['contacts/getContact'](sender.id);
    },

    isActiveChat() {
      return this.currentChat.id === this.chat.id;
    },

    unreadCount() {
      return this.chat.unread_count;
    },

    hasUnread() {
      return this.unreadCount > 0;
    },

    isInboxNameVisible() {
      // 不再隐藏inbox name, 打开inbox列表的情况下也常驻inbox name
      return true;
    },

    lastMessageInChat() {
      return getLastMessage(this.chat);
    },

    inbox() {
      const { inbox_id: inboxId } = this.chat;
      const stateInbox = this.$store.getters['inboxes/getInbox'](inboxId);
      return stateInbox;
    },

    showInboxName() {
      return (
        !this.hideInboxName &&
        this.isInboxNameVisible &&
        this.inboxesList.length > 1
      );
    },
    inboxName() {
      const stateInbox = this.inbox;
      return stateInbox.name || '';
    },
    hasSlaPolicyId() {
      return this.chat?.sla_policy_id;
    },
    // 评价分数
    rating() {
      return this.chat?.custom_attributes?.ub_conversation_rating ?? 0;
    },
    // Inbox头像相关
    computedInboxClass() {
      const { phone_number: phoneNumber, channel_type: type, name } = this.inbox;
      return getInboxClassByType(type, phoneNumber, name);
    },
    computedInboxAvatar() {
      const { ub_avatar_url } = this.inbox;
      return ub_avatar_url;
    },
    // Team信息
    teamName() {
      return this.chatMetadata.team?.name || '';
    },
    // 用户在线状态
    userOnlineStatus() {
      return this.currentContact?.availability_status || 'offline';
    },
    // 状态显示映射
    statusDisplayText() {
      if (this.chat.status === this.constants.STATUS_TYPE.SNOOZED) {
        return 'on hold';
      }
      return this.chat.status;
    },
    // 显示评分还是未读数
    showRating() {
      return this.chat.status === this.constants.STATUS_TYPE.RESOLVED && this.rating > 0;
    },
    // 时间展示逻辑
    timeDisplayText() {
      // 如果使用TimeAgo组件，返回null
      if (this.shouldUseTimeAgo) {
        return null;
      }
      // 否则返回当前计算的时间文本
      return this.currentTimeDisplayText || '';
    },
    // 是否使用TimeAgo组件
    shouldUseTimeAgo() {
      const status = this.chat.status;
      
      if (status === this.constants.STATUS_TYPE.RESOLVED) {
        // 已解决状态：保持原有TimeAgo逻辑
        return true;
      } else if (status === this.constants.STATUS_TYPE.PENDING) {
        // 待处理状态：使用自定义时间显示
        return false;
      } else if (status === this.constants.STATUS_TYPE.OPEN) {
        // 开放状态：根据wait_response字段判断
        const waitResponse = this.chat.wait_response;
        if (waitResponse && waitResponse.wait_target && waitResponse.wait_start_time) {
          // 有wait_response信息，使用自定义时间显示
          return false;
        }
        // 如果没有wait_response信息，回退到TimeAgo
        return true;
      }
      
      return true; // 默认使用TimeAgo
    },
    // 是否显示时钟图标
    shouldShowClockIcon() {
      const status = this.chat.status;
      if (status === this.constants.STATUS_TYPE.OPEN) {
        const waitResponse = this.chat.wait_response;
        return waitResponse && waitResponse.wait_target === 'User' && waitResponse.wait_start_time;
      }
      return false;
    },
    // 时间hover提示文本
    timeTooltipText() {
      const status = this.chat.status;
      
      if (status === this.constants.STATUS_TYPE.PENDING) {
        return 'Time since the user entered the chat';
      } else if (status === this.constants.STATUS_TYPE.OPEN) {
        const waitResponse = this.chat.wait_response;
        if (waitResponse && waitResponse.wait_target) {
          if (waitResponse.wait_target === 'Contact') {
            return 'Time waiting for the user to respond';
          } else if (waitResponse.wait_target === 'User') {
            return 'Time waiting for our reply';
          }
        }
      }
      
      return null; // 其他状态不显示tooltip
    },
    // 是否显示时间tooltip
    shouldShowTimeTooltip() {
      return this.timeTooltipText !== null;
    },
  },
  watch: {
    // 监听状态变化，重新设置定时器
    'chat.status'() {
      this.setupTimeUpdateInterval();
    },
    'chat.wait_response'() {
      this.setupTimeUpdateInterval();
    },
  },
  mounted() {
    this.setupIntersectionObserver();
    this.setupTimeUpdateInterval();
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
    }
  },
  methods: {
    setupIntersectionObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isVisible) {
              this.isVisible = true;
              
              // 检查是否已经上报过这个对话ID
              if (!this.reportedConversations.has(this.chat.id)) {
                this.reportedConversations.add(this.chat.id);
                window.Bee?.logBatchAuto({
                  target_type: '',
                  code: 'app-756df8e2',
                  extend: {
                    account_id: this.accountId,
                    inbox_id: this.inbox.id,
                    user_id: this.currentUser.id,
                    user_name: this.currentUser.name,
                    conversation_id: this.chat.id,
                    chat_status: this.chat.status,
                    contact_name: this.currentContact?.name,
                  }
                });
              }
            } else if (!entry.isIntersecting && this.isVisible) {
              this.isVisible = false;
            }
          });
        },
        {
          threshold: 0.1, // 当10%的元素可见时触发
          rootMargin: '0px'
        }
      );
      
      // 开始观察当前元素
      this.$nextTick(() => {
        if (this.$el) {
          this.observer.observe(this.$el);
        }
      });
    },
    onCardClick(e) {
      const { activeInbox, chat } = this;
      const path = frontendURL(
        conversationUrl({
          accountId: this.accountId,
          activeInbox,
          id: chat.id,
          label: this.activeLabel,
          teamId: this.teamId,
          foldersId: this.foldersId,
          conversationType: this.conversationType,
        })
      );

      if (e.metaKey || e.ctrlKey) {
        window.open(
          window.chatwootConfig.hostURL + path,
          '_blank',
          'noopener noreferrer nofollow'
        );
        return;
      }
      if (this.isActiveChat) {
        return;
      }

      router.push({ path });
    },
    onCardHover() {
      this.hovered = !this.hideThumbnail;
    },
    onCardLeave() {
      this.hovered = false;
    },
    onSelectConversation(checked) {
      const action = checked ? 'selectConversation' : 'deSelectConversation';
      this.$emit(action, this.chat.id, this.inbox.id);
    },
    openContextMenu(e) {
      if (!this.enableContextMenu) return;
      e.preventDefault();
      this.$emit('contextMenuToggle', true);

      // 获取视窗尺寸
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      // 获取点击位置
      const clickX = e.pageX || e.clientX;
      const clickY = e.pageY || e.clientY;

      // 根据点击位置相对于视窗边界的距离来判断菜单应该显示的位置
      // 距离底部的安全距离
      const safeBottomDistance = 230; // 假设菜单最大高度
      // 距离右侧的安全距离
      const safeRightDistance = 200; // 假设菜单最大宽度

      // 如果点击位置距离底部小于安全距离，则向上显示菜单
      if (windowHeight - clickY < safeBottomDistance) {
        this.contextMenu.y = clickY - safeBottomDistance;
      } else {
        this.contextMenu.y = clickY;
      }

      // 如果点击位置距离右侧小于安全距离，则向左显示菜单
      if (windowWidth - clickX < safeRightDistance) {
        this.contextMenu.x = clickX - safeRightDistance;
      } else {
        this.contextMenu.x = clickX;
      }

      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.$emit('contextMenuToggle', false);
      this.showContextMenu = false;
      this.contextMenu.x = null;
      this.contextMenu.y = null;
    },
    async onUpdateConversation(status, snoozedUntil) {
      if (status === wootConstants.STATUS_TYPE.RESOLVED) {
        this.pendingSnoozedUntil = snoozedUntil;
        this.showResolveConfirm = true;
        return;
      }
      this.closeContextMenu();
      this.$emit(
        'updateConversationStatus',
        this.chat.id,
        status,
        snoozedUntil
      );
    },
    confirmResolve() {
      this.showResolveConfirm = false;
      this.closeContextMenu();
      this.$emit(
        'updateConversationStatus',
        this.chat.id,
        wootConstants.STATUS_TYPE.RESOLVED,
        this.pendingSnoozedUntil
      );
    },
    cancelResolve() {
      this.showResolveConfirm = false;
    },
    async onAssignAgent(agent) {
      this.$emit('assignAgent', agent, [this.chat.id]);
      this.closeContextMenu();
    },
    async onAssignLabel(label) {
      this.$emit('assignLabel', [label.title], [this.chat.id]);
      this.closeContextMenu();
    },
    async onAssignTeam(team) {
      this.$emit('assignTeam', team, this.chat.id);
      this.closeContextMenu();
    },
    async markAsUnread() {
      this.$emit('markAsUnread', this.chat.id);
      this.closeContextMenu();
    },
    async assignPriority(priority) {
      this.$emit('assignPriority', priority, this.chat.id);
      this.closeContextMenu();
    },
    // 计算从指定时间到现在的时长（用于pending状态和等待用户回复）
    getDurationFromNow(startTime) {
      if (!startTime) return '';
      
      // 如果startTime是秒级时间戳，转换为毫秒级
      const start = new Date(typeof startTime === 'number' && startTime < 10000000000 ? startTime * 1000 : startTime);
      const now = new Date();
      const diffMs = now - start;
      
      const seconds = Math.floor(diffMs / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      
      if (hours > 0) {
        return `more than ${hours} hour${hours > 1 ? 's' : ''}`;
      } else if (minutes > 1) {
        return `${minutes} minutes`;
      } else if (minutes === 1) {
        return '1 minute';
      } else {
        return 'Less than 1 minute';
      }
    },
    // 计算从指定时间开始的经过时间（用于等待客服回复的计时器格式）
    getElapsedTime(startTime) {
      if (!startTime) return '';
      
      // 如果startTime是秒级时间戳，转换为毫秒级
      const start = new Date(typeof startTime === 'number' && startTime < 10000000000 ? startTime * 1000 : startTime);
      const now = new Date();
      const diffMs = now - start;
      
      const totalSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      
      if (hours > 0) {
        // 超过1小时：XX:XX:XX
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      } else {
        // 59:59秒内：XX:XX
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
    },
    // 计算当前时间显示文本
    calculateTimeDisplayText() {
      const status = this.chat.status;
      
      if (status === this.constants.STATUS_TYPE.PENDING) {
        // 待处理状态：显示从创建时间到现在的时长
        return this.getDurationFromNow(this.chat.created_at);
      } else if (status === this.constants.STATUS_TYPE.OPEN) {
        // 开放状态：根据wait_response字段判断
        const waitResponse = this.chat.wait_response;
        if (waitResponse && waitResponse.wait_target && waitResponse.wait_start_time) {
          if (waitResponse.wait_target === 'User') {
            // 等待客服回复：使用计时器格式
            return this.getElapsedTime(waitResponse.wait_start_time);
          } else if (waitResponse.wait_target === 'Contact') {
            // 等待用户回复：使用时长格式
            return this.getDurationFromNow(waitResponse.wait_start_time);
          }
        }
      }
      
      return '';
    },
    // 设置时间更新定时器
    setupTimeUpdateInterval() {
      // 清除之前的定时器
      if (this.timeUpdateInterval) {
        clearInterval(this.timeUpdateInterval);
        this.timeUpdateInterval = null;
      }
      
      // 只有当需要显示自定义时间时才启动定时器
      if (!this.shouldUseTimeAgo) {
        // 初始化时间显示文本
        this.currentTimeDisplayText = this.calculateTimeDisplayText();
        
        this.timeUpdateInterval = setInterval(() => {
          // 更新时间显示文本
          this.currentTimeDisplayText = this.calculateTimeDisplayText();
        }, 1000); // 每秒更新一次
      } else {
        // 如果使用TimeAgo，清空自定义时间文本
        this.currentTimeDisplayText = '';
      }
    },
  },
};
</script>

<template>
  <div
    class="relative flex items-center flex-grow-0 flex-shrink-0 w-auto max-w-full px-4 py-3 border-t-0 border-b-0 border-l-2 border-r-0 border-transparent border-solid cursor-pointer conversation conversation-card-v2 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group"
    :class="{
      'active animate-card-select bg-n-alpha-1 dark:bg-n-alpha-3 border-n-weak':
        isActiveChat,
      'unread-chat': hasUnread,
      'conversation-selected': selected,
    }"
    @mouseenter="onCardHover"
    @mouseleave="onCardLeave"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <!-- 分割线 -->
    <div class="absolute bottom-0 left-[68px] right-0 h-px" style="background-color: #F0F0F3;"></div>
    <!-- 选择框 -->
    <label v-if="hovered || selected" class="checkbox-wrapper" @click.stop>
      <input
        :value="selected"
        :checked="selected"
        class="checkbox"        type="checkbox"
        @change="onSelectConversation($event.target.checked)"
      />
    </label>

    <!-- 左侧：Inbox头像 + 用户在线状态 -->
    <div class="flex-shrink-0 mr-3.5 relative">
      <div class="inbox-avatar-container relative">
        <!-- Inbox头像 -->
        <div class="w-[38px] h-[38px] rounded-lg bg-orange-500 flex items-center justify-center text-white font-medium text-sm">
          <img 
            v-if="computedInboxAvatar" 
            :src="computedInboxAvatar" 
            class="w-full h-full rounded-lg object-cover"
            alt="Inbox"
          />
          <fluent-icon
            v-else
            :icon="computedInboxClass"
            size="20"
            class="text-white"
          />
        </div>
        <!-- 用户在线状态指示器 -->
        <div
          v-if="userOnlineStatus === 'online'"
          class="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white dark:border-slate-800"
        />
      </div>
    </div>

    <!-- 中间：用户信息 + Team + Agent -->
    <div class="flex-1 min-w-0 mr-3">
      <!-- 用户名 -->
      <h4
        class="text-sm font-medium text-n-slate-12 mb-1 truncate"
        :class="hasUnread ? 'font-semibold' : 'font-medium'"
      >
        {{ currentContact.name }}
      </h4>
      
      <!-- Team信息 -->
      <div v-if="teamName" class="mb-1">
        <span class="inline-flex items-center text-xs font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap overflow-hidden text-ellipsis max-w-full" style="padding: 2px 8px; border: 1px solid #D9D9D9; border-radius: 4px;">
          {{ teamName }}
        </span>
      </div>
      
      <!-- Agent信息 -->
      <div v-if="assigneeDisplayName && chat.status !== constants.STATUS_TYPE.PENDING" class="flex items-center text-xs text-n-slate-11">
        <SvgIcon name="assignee" size="12" class="mr-1 flex-shrink-0" />
        <span class="truncate">{{ assigneeDisplayName }}</span>
      </div>
    </div>

    <!-- 右侧：状态 + 时间 + 评分/未读数 -->
    <div class="flex-shrink-0 text-right">
      <!-- 会话状态 -->
      <div
        v-if="chat.status"
        class="status-badge text-xxs font-medium px-2 py-0.5 rounded-full opacity-80 inline-block mb-2"
        :class="{
          'bg-purple-50 text-purple-600 border border-purple-100':
            chat.status === constants.STATUS_TYPE.OPEN ||
            chat.status === constants.STATUS_TYPE.ALL,
          'bg-amber-50 text-amber-600 border border-amber-100':
            chat.status === constants.STATUS_TYPE.PENDING,
          'bg-slate-50 text-slate-600 border border-slate-100':
            chat.status === constants.STATUS_TYPE.SNOOZED,
          'text-green-600 border border-green-100':
            chat.status === constants.STATUS_TYPE.RESOLVED,
        }"
        :style="{ height: '21px' }"
      >
        {{ statusDisplayText }}
      </div>
      
      <!-- 时间信息 -->
      <div class="mb-1 text-xs text-n-slate-11">
        <TimeAgo
          v-if="shouldUseTimeAgo"
          :last-activity-timestamp="chat.timestamp"
          :created-at-timestamp="chat.created_at"
          :conversation-status="chat.status"
        />
        <div
          v-else-if="timeDisplayText"
          v-tooltip.top="shouldShowTimeTooltip ? {
            content: timeTooltipText,
            delay: { show: 300, hide: 0 },
          } : null"
          class="flex items-center"
        >
          <SvgIcon v-if="shouldShowClockIcon" name="clock" size="12" class="mr-1 flex-shrink-0" />
          <span>{{ timeDisplayText }}</span>
        </div>
      </div>
      
      <!-- 评分或未读数 -->
      <div class="flex items-center justify-end">
        <!-- 评分（已解决且有评分时显示） -->
        <div v-if="showRating" class="flex items-center text-xs">
          <span class="mr-1">{{ rating }}</span>
          <StarIcon color="#ffb623" fill="#ffb623" height="14" width="14" />
        </div>
        <!-- 未读数（未解决时显示） -->
        <div v-else-if="hasUnread" class="flex items-center justify-center w-4 h-4 bg-teal-500 text-white text-[10px] font-normal rounded-full">
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </div>
      </div>
    </div>

    <!-- 右键菜单 -->
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @assign-priority="assignPriority"
      />
    </ContextMenu>

    <Teleport to="body">
      <Modal :show="showResolveConfirm" :on-close="cancelResolve">
        <div class="flex flex-col p-8">
          <h2 class="text-base font-semibold text-slate-800 dark:text-slate-50 mb-4">
            Are you sure you want to resolve this conversation?
          </h2>
          <div class="flex justify-end gap-2">
            <woot-button variant="clear" @click="cancelResolve">
              Cancel
            </woot-button>
            <woot-button variant="primary" @click="confirmResolve">
              Resolve
            </woot-button>
          </div>
        </div>
      </Modal>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.conversation {
  &.conversation-selected {
    @apply bg-n-slate-2 dark:bg-n-slate-3;
  }

  .checkbox-wrapper {
    height: 2.5rem;
    width: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    margin-top: 0;
    transition: background-color 0.2s ease-in-out;

    &:hover {
      background-color: var(--color-woot-100);
    }

    .dark &:hover {
      background-color: var(--color-woot-800);
    }

    input[type='checkbox'] {
      margin: 0;
      cursor: pointer;
    }
  }

  .inbox-avatar-container {
    .w-10.h-10 {
      transition: all 0.2s ease-in-out;
    }
  }
}

// 暗色模式下的状态标签样式调整
.dark {
  .conversation {
    .bg-purple-100 {
      background-color: rgb(88 28 135);
    }
    .text-purple-700 {
      color: rgb(196 181 253);
    }
    .bg-amber-100 {
      background-color: rgb(120 53 15);
    }
    .text-amber-700 {
      color: rgb(252 211 77);
    }
    .bg-slate-100 {
      background-color: rgb(51 65 85);
    }
    .text-slate-700 {
      color: rgb(203 213 225);
    }
    .bg-green-100 {
      background-color: rgb(20 83 45);
    }
    .text-green-700 {
      color: rgb(134 239 172);
    }
  }
}
</style>
